import { useEffect, useRef, useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { ProcedureFlowBuilder, type BuilderStep } from '@/components/procedures/ProcedureFlowBuilder'
import type { FormDefinition } from '@/types/cgt'

interface Props {
  procedureTemplateId: string
  procedureName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Reused from the caller so this doesn't refetch every form definition. */
  definitions?: FormDefinition[]
}

/**
 * Read-only popup of a template's flow (same DAG as `/procedure-templates/:id/sequence`,
 * with dragging/connecting/deleting disabled) — for exploring the template a
 * patient's procedure is following, not editing it.
 */
export function ProcedureFlowPreviewDialog({
  procedureTemplateId,
  procedureName,
  open,
  onOpenChange,
  definitions,
}: Props) {
  const [steps, setSteps] = useState<BuilderStep[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open || !procedureTemplateId || loadedForRef.current === procedureTemplateId) return
    setLoading(true)
    setError(null)
    procedureTemplatesApi
      .get(procedureTemplateId)
      .then(proc => {
        loadedForRef.current = procedureTemplateId
        setSteps(
          proc.steps.map(s => ({
            key: s.stepId,
            formDefinitionId: s.formDefinitionId,
            formName: s.formName,
            predecessorKeys: new Set(s.predecessors.map(p => p.stepId)),
          })),
        )
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load the procedure flow.'))
      .finally(() => setLoading(false))
  }, [open, procedureTemplateId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-hidden sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle>{procedureName}</DialogTitle>
          <DialogDescription>
            The form flow this procedure is following. View-only — open the template's Sequence page to edit it.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className='flex h-[520px] items-center justify-center text-sm text-muted-foreground'>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Loading flow…
          </div>
        )}

        {!loading && error && (
          <div className='flex h-[520px] flex-col items-center justify-center gap-2 text-sm text-destructive'>
            <AlertCircle className='h-5 w-5' />
            {error}
          </div>
        )}

        {!loading && !error && steps && (
          <ProcedureFlowBuilder steps={steps} definitions={definitions} readOnly className='h-[65vh]' />
        )}
      </DialogContent>
    </Dialog>
  )
}
